import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaFire, 
  FaStar, 
  FaTrophy, 
  FaMedal, 
  FaChartLine,
  FaBook,
  FaClock,
  FaBullseye,
  FaArrowRight,
  FaPlay,
  FaCheckCircle,
  FaGem,
  FaCrown,
  FaRocket,
  FaHeart,
  FaBrain,
  FaLightbulb,
  FaGraduationCap
} from 'react-icons/fa';

const LXDDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const userStats = {
    level: 12,
    xp: 3450,
    xpToNext: 5000,
    streak: 15,
    hearts: 5,
    gems: 250,
    coursesCompleted: 8,
    totalLessons: 45,
    accuracy: 87
  };

  const achievements = [
    { id: 1, name: 'First Steps', description: 'Complete your first lesson', icon: <FaGraduationCap />, unlocked: true, progress: 100 },
    { id: 2, name: 'Week Warrior', description: 'Maintain a 7-day streak', icon: <FaFire />, unlocked: true, progress: 100 },
    { id: 3, name: 'Perfect Score', description: 'Get 100% on a lesson', icon: <FaStar />, unlocked: true, progress: 100 },
    { id: 4, name: 'Bookworm', description: 'Complete 10 lessons', icon: <FaBook />, unlocked: true, progress: 100 },
    { id: 5, name: 'Speed Demon', description: 'Complete a lesson in under 2 minutes', icon: <FaRocket />, unlocked: false, progress: 65 },
    { id: 6, name: 'Master Mind', description: 'Reach level 20', icon: <FaBrain />, unlocked: false, progress: 60 },
    { id: 7, name: 'Gem Collector', description: 'Earn 500 gems', icon: <FaGem />, unlocked: false, progress: 50 },
    { id: 8, name: 'Heart Keeper', description: 'Never lose a heart for 30 days', icon: <FaHeart />, unlocked: false, progress: 30 }
  ];

  const recentActivity = [
    { type: 'lesson', title: 'Web Development Basics', xp: 50, time: '2 hours ago', icon: <FaBook /> },
    { type: 'achievement', title: 'Week Warrior', xp: 100, time: '1 day ago', icon: <FaTrophy /> },
    { type: 'lesson', title: 'React Fundamentals', xp: 75, time: '2 days ago', icon: <FaBook /> },
    { type: 'streak', title: '15 Day Streak', xp: 150, time: '3 days ago', icon: <FaFire /> }
  ];

  const leaderboard = [
    { rank: 1, name: 'Abebe Tesfaye', xp: 12500, avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', badge: <FaCrown className="text-yellow-500" /> },
    { rank: 2, name: 'Selamawit Haile', xp: 11200, avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100', badge: <FaMedal className="text-gray-400" /> },
    { rank: 3, name: 'Dawit Mekonnen', xp: 10800, avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100', badge: <FaMedal className="text-orange-400" /> },
    { rank: 4, name: 'You', xp: 3450, avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100', isUser: true },
    { rank: 5, name: 'Tigist Bekele', xp: 9800, avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100' },
    { rank: 6, name: 'Kibru Tadesse', xp: 9200, avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100' }
  ];

  const weeklyProgress = [
    { day: 'Mon', xp: 120 },
    { day: 'Tue', xp: 200 },
    { day: 'Wed', xp: 150 },
    { day: 'Thu', xp: 280 },
    { day: 'Fri', xp: 180 },
    { day: 'Sat', xp: 320 },
    { day: 'Sun', xp: 250 }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-20">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Learning Dashboard</h1>
          <p className="text-gray-600">Track your progress and achievements</p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <FaStar className="text-3xl opacity-80" />
              <span className="text-sm opacity-80">Level {userStats.level}</span>
            </div>
            <div className="text-3xl font-bold mb-1">{userStats.xp.toLocaleString()}</div>
            <div className="text-sm opacity-80">Total XP</div>
            <div className="mt-3 bg-white/20 rounded-full h-2">
              <div 
                className="bg-white rounded-full h-2" 
                style={{ width: `${(userStats.xp / userStats.xpToNext) * 100}%` }}
              />
            </div>
            <div className="text-xs mt-1 opacity-80">{userStats.xpToNext - userStats.xp} XP to next level</div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <FaFire className="text-3xl opacity-80" />
              <span className="text-sm opacity-80">Current</span>
            </div>
            <div className="text-3xl font-bold mb-1">{userStats.streak}</div>
            <div className="text-sm opacity-80">Day Streak</div>
            <div className="text-xs mt-3 opacity-80">Keep it going! 🔥</div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <FaHeart className="text-3xl opacity-80" />
              <span className="text-sm opacity-80">Available</span>
            </div>
            <div className="text-3xl font-bold mb-1">{userStats.hearts}</div>
            <div className="text-sm opacity-80">Hearts</div>
            <div className="text-xs mt-3 opacity-80">Refills in 8 hours</div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <FaGem className="text-3xl opacity-80" />
              <span className="text-sm opacity-80">Balance</span>
            </div>
            <div className="text-3xl font-bold mb-1">{userStats.gems}</div>
            <div className="text-sm opacity-80">Gems</div>
            <div className="text-xs mt-3 opacity-80">Earn more by learning!</div>
          </motion.div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b">
          {['overview', 'achievements', 'leaderboard', 'progress'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === tab
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-purple-600'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          key={activeTab}
        >
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Continue Learning */}
              <motion.div variants={itemVariants} className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Continue Learning</h2>
                <div className="space-y-4">
                  {[
                    { title: 'Web Development Bootcamp', progress: 65, nextLesson: 'React Components', image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=200' },
                    { title: 'Data Science with Python', progress: 40, nextLesson: 'Pandas Basics', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200' },
                    { title: 'Digital Marketing', progress: 85, nextLesson: 'SEO Advanced', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=200' }
                  ].map((course, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all cursor-pointer"
                    >
                      <img src={course.image} alt={course.title} className="w-16 h-16 rounded-lg object-cover" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{course.title}</h3>
                        <p className="text-sm text-gray-600">Next: {course.nextLesson}</p>
                        <div className="mt-2 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 rounded-full h-2" 
                            style={{ width: `${course.progress}%` }}
                          />
                        </div>
                      </div>
                      <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all flex items-center gap-2">
                        <FaPlay /> Continue
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Recent Activity */}
              <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Activity</h2>
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">
                        {activity.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800 text-sm">{activity.title}</div>
                        <div className="text-xs text-gray-500">{activity.time}</div>
                      </div>
                      <div className="text-purple-600 font-semibold text-sm">+{activity.xp} XP</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {achievements.map((achievement) => (
                <motion.div
                  key={achievement.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  className={`bg-white rounded-2xl p-6 shadow-lg ${
                    achievement.unlocked ? '' : 'opacity-60'
                  }`}
                >
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                    achievement.unlocked 
                      ? 'bg-gradient-to-br from-yellow-400 to-orange-500' 
                      : 'bg-gray-200'
                  }`}>
                    <span className={`text-2xl ${achievement.unlocked ? 'text-white' : 'text-gray-400'}`}>
                      {achievement.icon}
                    </span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{achievement.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{achievement.description}</p>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className={`rounded-full h-2 ${achievement.unlocked ? 'bg-green-500' : 'bg-purple-500'}`}
                      style={{ width: `${achievement.progress}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{achievement.progress}% complete</div>
                  {achievement.unlocked && (
                    <div className="mt-3 flex items-center gap-2 text-green-600 text-sm font-semibold">
                      <FaCheckCircle /> Unlocked!
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Weekly Leaderboard</h2>
              <div className="space-y-4">
                {leaderboard.map((user) => (
                  <motion.div
                    key={user.rank}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                    className={`flex items-center gap-4 p-4 rounded-xl ${
                      user.isUser ? 'bg-purple-50 border-2 border-purple-500' : 'bg-gray-50 hover:bg-gray-100'
                    } transition-all`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      user.rank <= 3 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' : 'bg-gray-300 text-gray-700'
                    }`}>
                      {user.rank}
                    </div>
                    <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800 flex items-center gap-2">
                        {user.name}
                        {user.badge}
                      </div>
                      {user.isUser && <span className="text-xs text-purple-600">You</span>}
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-purple-600">{user.xp.toLocaleString()} XP</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Weekly Progress</h2>
                <div className="flex items-end justify-between h-48 gap-2">
                  {weeklyProgress.map((day, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-gradient-to-t from-purple-600 to-blue-500 rounded-t-lg transition-all hover:opacity-80"
                        style={{ height: `${(day.xp / 350) * 100}%` }}
                      />
                      <div className="text-xs text-gray-600 mt-2">{day.day}</div>
                      <div className="text-xs text-gray-400">{day.xp} XP</div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Learning Statistics</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <FaBook className="text-purple-600" />
                      <span className="text-gray-700">Lessons Completed</span>
                    </div>
                    <span className="font-bold text-gray-800">{userStats.totalLessons}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <FaBullseye className="text-purple-600" />
                      <span className="text-gray-700">Courses Completed</span>
                    </div>
                    <span className="font-bold text-gray-800">{userStats.coursesCompleted}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <FaChartLine className="text-purple-600" />
                      <span className="text-gray-700">Overall Accuracy</span>
                    </div>
                    <span className="font-bold text-gray-800">{userStats.accuracy}%</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <FaClock className="text-purple-600" />
                      <span className="text-gray-700">Time Spent</span>
                    </div>
                    <span className="font-bold text-gray-800">24h 30m</span>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white text-center"
        >
          <h2 className="text-2xl font-bold mb-2">Ready to Learn More?</h2>
          <p className="text-white/90 mb-4">Continue your learning journey and earn more XP!</p>
          <Link
            to="/courses"
            className="inline-block bg-white text-purple-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all"
          >
            Browse Courses <FaArrowRight className="inline ml-2" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default LXDDashboard;
